import SelectBuscable from "@/shared/components/SelectBuscable";
import { ZONA_DE_OPERACION, formatIsoEnZona } from '@/shared/lib/dateUtils'
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wrench,
  AlertTriangle,
  CircleAlert,
  ArrowUpRight,
  PackagePlus,
  Undo2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Settings,
  CheckCheck,
  History,
} from "lucide-react";
import {
  almacenService,
  type AlmacenDashboardData,
} from '@/features/almacen/api/almacenService';
import KpiCard from '@/features/rh/components/dashboard/KpiCard';
import { useAuth } from '@/shared/hooks/useAuth';
import { userHasPermission } from '@/shared/lib/roleUtils';
import { ALMACEN } from '@/constants/permissions';
import { Revelar } from '@/shared/components/Revelar';
import { useEnPantalla } from '@/shared/hooks/useEnPantalla';

const MOVEMENT_LABEL: Record<string, string> = {
  assignment:   "Asignación",
  reassignment: "Reasignación",
  return:       "Devolución",
};

// ─── Tool image / icon ────────────────────────────────────────────────────────

/** Shows the tool image when available, falls back to the branded icon. */
/** Renders image + icon fallback pair so onError can toggle between them. */
// Aviso cuando la carga fallo: distinto de "no hay datos", que es lo que se
// mostraba antes y hacia imposible notar el fallo.
const SeccionError = ({ onRetry }: { onRetry: () => void }) => (
  <div className="flex flex-col items-center justify-center min-h-[120px] gap-1 text-center">
    <p className="text-sm text-gray-500">No se pudo cargar esta información.</p>
    <button
      type="button"
      onClick={onRetry}
      className="text-sm font-medium text-[#315FB9] hover:underline"
    >
      Reintentar
    </button>
  </div>
);

const ToolImageWithFallback = ({
  imageUrl,
  nombre,
  className = "",
  style,
}: {
  imageUrl?: string;
  nombre?: string;
  className?: string;
  style?: React.CSSProperties;
}) => (
  <>
    {imageUrl && (
      <img
        src={imageUrl}
        alt={nombre ?? "herramienta"}
        className={`rounded-lg object-cover ${className}`}
        style={style}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = "none";
          const next = e.currentTarget.nextElementSibling as HTMLElement | null;
          if (next) next.style.display = "flex";
        }}
      />
    )}
    <div
      className={`rounded-lg flex items-center justify-center ${className}`}
      style={{
        backgroundColor: "#E0E7F5",
        display: imageUrl ? "none" : "flex",
        ...style,
      }}
    >
      <Wrench className="w-5 h-5" style={{ color: "#315FB9" }} />
    </div>
  </>
);

// ─── Almacen selector ─────────────────────────────────────────────────────────

const AlmacenSelector = ({
  almacenes,
  selected,
  onSelect,
}: {
  almacenes: { id: string; nombre: string }[];
  selected: string;
  onSelect: (id: string) => void;
}) => (
  <SelectBuscable
    variante="listado"
    ancho="contenido"
    etiqueta="Todos los almacenes"
    todos="Todos los almacenes"
    canDelete={false}
    value={selected}
    onChange={onSelect}
    placeholderDeBusqueda="Buscar almacén…"
    opciones={almacenes.map((a) => ({ value: a.id, label: a.nombre }))}
  />
);

// ─── Constants ────────────────────────────────────────────────────────────────

const ITEMS_PER_PAGE = 6;
const MAX_ALERTAS = 5;
const MAX_RECIENTES = 4;

// ─── Main dashboard ───────────────────────────────────────────────────────────

const AlmacenDashboard = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const permissions = user?.rbac?.effectivePermissions ?? [];
  const has = (p: string) => userHasPermission(p, permissions);
  const isMaster = user?.rbac?.isMaster ?? false;

  const canViewDashboard =
    isMaster ||
    has(ALMACEN.DASHBOARD_VER) ||
    permissions.some((p) => p.startsWith(ALMACEN._PREFIX));
  // Con permiso de dashboard ve el detalle completo; sin él, solo las KPI cards de arriba
  const hasFullDashboard = isMaster || has(ALMACEN.DASHBOARD_VER);
  const canAsignar =
    isMaster || has(ALMACEN.MOVIMIENTOS_ASIGNAR);
  const canDevolver =
    isMaster || has(ALMACEN.MOVIMIENTOS_DEVOLVER);
  const canReportar = isMaster || has(ALMACEN.REPORTES_CREAR);
  const canRegistrar = isMaster || has(ALMACEN.HERRAMIENTAS_CREAR);

  // La cascada de la tabla espera a que el bloque se vea; si no, se gasta
  // mientras el usuario sigue arriba leyendo las tarjetas.
  const tablaHistorial = useEnPantalla<HTMLDivElement>();

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<AlmacenDashboardData | null>(null);
  const [almacenes, setAlmacenes] = useState<{ id: string; nombre: string }[]>([]);
  const [selectedAlmacen, setSelectedAlmacen] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loadError, setLoadError] = useState(false);

  const loadData = useCallback(async () => {
    // Sin permisos resueltos todavia, hasFullDashboard seria false y se pediria
    // la version acotada aunque el usuario si tenga acceso completo: quedaban
    // las secciones vacias hasta el siguiente render.
    if (authLoading) return;
    setIsLoading(true);
    setLoadError(false);
    try {
      // Sin permiso de dashboard, /almacen/dashboard responde 403 y sus KPIs son
      // globales de todos modos. Las cards se arman entonces con las
      // herramientas que el usuario sí puede ver: el back ya acota ese listado
      // a las asignadas cuando no tiene el "ver" general, así que los números
      // hablan de lo suyo y no del almacén completo.
      if (!hasFullDashboard) {
        const misHerramientas = await almacenService.getHerramientasVisibles();
        const cuenta = (...estados: string[]) =>
          misHerramientas.filter((h) => estados.includes(h.status)).length;
        setData({
          metrics: {
            totalHerramientas: misHerramientas.length,
            enBuenEstado:      cuenta('good', 'new_equipment'),
            conReporteDano:    cuenta('damaged', 'missing'),
            enMantenimiento:   cuenta('in_maintenance'),
          },
          alertas: [],
          historialReciente: [],
          herramientasRecientes: [],
        });
        return;
      }

      const [dashData, almacenesList] = await Promise.all([
        almacenService.getDashboardData(selectedAlmacen),
        almacenService.getAlmacenes(),
      ]);
      setData(dashData);
      if (almacenesList.length > 0) setAlmacenes(almacenesList);
    } catch {
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [selectedAlmacen, hasFullDashboard, authLoading]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-[#253579] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!canViewDashboard) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-gray-500">
        No tienes permiso para ver este módulo.
      </div>
    );
  }

  const metrics = data?.metrics;
  const alertas = (data?.alertas ?? []).slice(0, MAX_ALERTAS);
  const historial = data?.historialReciente ?? [];
  const recientes = (data?.herramientasRecientes ?? []).slice(0, MAX_RECIENTES);

  // Pagination
  const totalPages = Math.ceil(historial.length / ITEMS_PER_PAGE);
  const pagedRows = historial.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  // "2025-01-15 09:30" — el mismo formato del diseno, pero en la zona de la
  // operacion: cortar la cadena ISO mostraba UTC crudo (S-03).
  const formatDateTime = (iso: string) => formatIsoEnZona(iso);

  const formatTimeShort = (iso: string) => {
    try {
      return new Date(iso)
        .toLocaleString("es-MX", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          timeZone: ZONA_DE_OPERACION,
        })
        .toLowerCase()
        .replace(",", "");
    } catch {
      return iso;
    }
  };

  return (
    <div className="mx-auto px-6 py-8">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inicio</h1>
          <p className="text-sm text-gray-500 mt-1">
            Consulta el estado general del almacén y accede rápidamente a las
            acciones más comunes
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-3">
          {/* <button
            onClick={loadData}
            disabled={isLoading}
            className="elevar inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-[#232F61] text-white hover:bg-[#1a2450] disabled:opacity-50 transition-colors"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${isLoading ? "girando" : ""}`}
            />
            {isLoading ? "Actualizando..." : "Actualizar"}
          </button> */}
          {almacenes.length > 0 && (
            <AlmacenSelector
              almacenes={almacenes}
              selected={selectedAlmacen}
              onSelect={(id) => {
                setSelectedAlmacen(id);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
      </div>

      {/* ── KPI Cards ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <div onClick={() => navigate("/almacen/registro")} className="cursor-pointer">
          <KpiCard
            title="Registradas"
            value={metrics?.totalHerramientas ?? 0}
            icon={<Wrench />}
            variant="purple"
            isLoading={isLoading}
          />
        </div>
        <div onClick={() => navigate("/almacen/registro")} className="cursor-pointer">
          <KpiCard
            title="En buen estado"
            value={metrics?.enBuenEstado ?? 0}
            icon={<CheckCheck />}
            variant="success"
            isLoading={isLoading}
          />
        </div>
        <div onClick={() => navigate("/almacen/reportes")} className="cursor-pointer">
          <KpiCard
            title="Con incidencia activa"
            value={metrics?.conReporteDano ?? 0}
            icon={<AlertTriangle />}
            variant="warning"
            isLoading={isLoading}
          />
        </div>
        <div onClick={() => navigate("/almacen/mantenimiento")} className="cursor-pointer">
          <KpiCard
            title="En mantenimiento"
            value={metrics?.enMantenimiento ?? 0}
            icon={<Settings />}
            variant="warning"
            isLoading={isLoading}
          />
        </div>
      </div>

      {hasFullDashboard && (
      <>
      {/* ── Row 1: Acciones rápidas (50%) | Alertas (50%) ──────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Acciones rápidas */}
        <Revelar className="h-full">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 h-full">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            Acciones rápidas
          </h3>
          {/* Dos por fila: cada tarjeta mide la mitad menos el hueco que las
              separa, y flex-wrap las acomoda cuando un permiso deja alguna
              fuera. */}
          <div className="flex flex-wrap gap-3">
            {canRegistrar && (
              <button
                onClick={() => navigate("/almacen/registro/crear")}
                className="w-[calc(50%-0.375rem)] flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 bg-white hover:bg-gray-50 transition-all text-left"
                style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "#E0E7F5" }}
                >
                  <Wrench className="w-5 h-5" style={{ color: "#315FB9" }} />
                </div>
                <span className="text-sm font-medium text-gray-800">
                  Registrar herramienta
                </span>
              </button>
            )}

            {canAsignar && (
              <button
                onClick={() => navigate("/almacen/asignacion/asignar")}
                className="w-[calc(50%-0.375rem)] flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 bg-white hover:bg-gray-50 transition-all text-left"
                style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "#E0E7F5" }}
                >
                  <PackagePlus
                    className="w-5 h-5"
                    style={{ color: "#315FB9" }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-800">
                  Asignar herramienta
                </span>
              </button>
            )}

            {canDevolver && (
              <button
                onClick={() => navigate("/almacen/devolucion/devolver")}
                className="w-[calc(50%-0.375rem)] flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 bg-white hover:bg-gray-50 transition-all text-left"
                style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "#E0E7F5" }}
                >
                  <Undo2 className="w-5 h-5" style={{ color: "#315FB9" }} />
                </div>
                <span className="text-sm font-medium text-gray-800">
                  Devolver herramienta
                </span>
              </button>
            )}

            {canReportar && (
              <button
                onClick={() => navigate("/almacen/reportes/nuevo")}
                className="w-[calc(50%-0.375rem)] flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 bg-white hover:bg-gray-50 transition-all text-left"
                style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "#E0E7F5" }}
                >
                  <AlertTriangle
                    className="w-5 h-5"
                    style={{ color: "#315FB9" }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-800">
                  Reportar incidencia
                </span>
              </button>
            )}
          </div>
        </div>
        </Revelar>

        {/* Alertas de mantenimiento */}
        <Revelar className="h-full">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 h-full flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">
              Alertas de mantenimiento
            </h3>
            <button
              onClick={() => navigate("/almacen/mantenimiento?tab=alertas")}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              title="Ver alertas de mantenimiento"
            >
              <ArrowUpRight className="w-[20px] h-[20px] text-gray-400" />
            </button>
          </div>

          <div className="space-y-3 min-h-[160px]">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-lg bg-gray-50"
                >
                  <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse flex-shrink-0" />
                  <div className="flex-1 space-y-1">
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                  </div>
                </div>
              ))
            ) : loadError ? (
              <SeccionError onRetry={loadData} />
            ) : alertas.length === 0 ? (
              <div className="flex items-center justify-center h-full min-h-[120px] text-sm text-gray-400">
                Sin alertas
              </div>
            ) : (
              alertas.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => navigate(`/almacen/mantenimiento?nuevo=${a.id}`)}
                  className="w-full flex items-center gap-3 p-2 pr-6 rounded-lg text-left hover:brightness-95 transition-all"
                  style={{
                    backgroundColor:
                      a.tipo === "vencido" ? "#FEEDED" : "#FEFBF1",
                  }}
                >
                  <ToolImageWithFallback
                    imageUrl={a.photoUrl ?? undefined}
                    nombre={a.herramienta}
                    className="flex-shrink-0"
                    style={{ width: 56, height: 49 }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {a.herramienta}
                    </p>
                    <p className="text-xs mt-0.5">
                      <span className="text-[#919498]">
                        {a.tipo === "vencido"
                          ? "Mantenimiento vencido"
                          : "Mantenimiento por vencer"}
                      </span>
                      {"  "}
                      <span className="text-[#919498]">
                        {(() => {
                          // Mantenimiento por horas de uso
                          if (a.horasAcumuladas != null && a.intervaloHoras != null) {
                            return `${a.horasAcumuladas} / ${a.intervaloHoras} hrs`
                          }
                          // Mantenimiento por calendario
                          if (a.diasRestantes != null) {
                            const d = a.diasRestantes
                            if (d < 0) {
                              const n = Math.abs(d)
                              return `venció hace ${n} ${n === 1 ? "día" : "días"}`
                            }
                            if (d === 0) return "vence hoy"
                            return `vence en ${d} ${d === 1 ? "día" : "días"}`
                          }
                          return ""
                        })()}
                      </span>
                    </p>
                  </div>
                  {a.tipo === "vencido" ? (
                    <CircleAlert
                      className="w-5 h-5 flex-shrink-0"
                      style={{ color: "#EF4444" }}
                    />
                  ) : (
                    <AlertTriangle
                      className="w-5 h-5 flex-shrink-0"
                      style={{ color: "#D4A112" }}
                    />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
        </Revelar>
      </div>

      {/* ── Row 2: Historial (2/3) | Herramientas recientes (1/3) ──────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Historial reciente. El col-span va en Revelar, que es el hijo real
            del grid: en el div de adentro no hacia nada y la tarjeta se
            quedaba en un tercio. */}
        <Revelar className="h-full lg:col-span-2">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 h-full">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-gray-500" />
              <h3 className="text-base font-semibold text-gray-900">
                Historial reciente
              </h3>
            </div>
            <button
              onClick={() => navigate("/almacen/historial/herramientas")}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              title="Ver historial completo"
            >
              <ArrowUpRight className="w-6 h-6 text-gray-400" />
            </button>
          </div>

          <div
            ref={tablaHistorial.ref}
            className="overflow-x-auto border border-[#E4E4E4] rounded-lg"
          >
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr
                  style={{
                    backgroundColor: "#FAF9FF",
                    borderBottom: "1px solid #E4E4E4",
                  }}
                >
                  {[
                    "Herramienta",
                    "Responsable",
                    "Proyecto asignado",
                    "Fecha y hora",
                    "Acción",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left py-3 px-2 text-sm font-semibold first:pl-3 last:pr-3"
                      style={{ color: "#534D59" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #E4E4E4" }}>
                      {Array.from({ length: 5 }).map((_, j) => (
                        <td key={j} className="py-3 px-2 first:pl-0">
                          <div className="h-4 bg-gray-100 rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : loadError ? (
                  <tr>
                    <td colSpan={5}>
                      <SeccionError onRetry={loadData} />
                    </td>
                  </tr>
                ) : pagedRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="py-10 text-center text-sm text-gray-400"
                    >
                      Sin movimientos registrados
                    </td>
                  </tr>
                ) : (
                  pagedRows.map((row, i) => (
                    <tr
                      key={row.id}
                      onClick={() => navigate("/almacen/historial/herramientas")}
                      className={`fila-cascada ${tablaHistorial.visible ? "" : "en-espera"} hover:bg-gray-50/50 transition-colors cursor-pointer`}
                      // El retraso es por fila, asi que no puede vivir en una
                      // clase de Tailwind. Las celdas lo heredan del <tr>.
                      style={{
                        borderBottom: "1px solid #E4E4E4",
                        animationDelay: `${i * 100}ms`,
                      }}
                    >
                      <td className="py-3 px-2 pl-3 text-gray-700">
                        {row.herramienta}
                      </td>
                      <td className="py-3 px-2 text-gray-600">
                        {row.responsable}
                      </td>
                      <td className="py-3 px-2 text-gray-600">
                        {row.proyectoAsignado}
                      </td>
                      <td className="py-3 px-2 text-gray-500 whitespace-nowrap">
                        {formatDateTime(row.fechaHora)}
                      </td>
                      <td className="py-3 px-2 text-gray-600">{MOVEMENT_LABEL[row.tipo] ?? row.tipo}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 mt-4 text-sm text-gray-600">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage === 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                      page === currentPage
                        ? "bg-[#232F61] text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}

              {totalPages > 5 && <span className="px-1">...</span>}
              {totalPages > 5 && (
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                    currentPage === totalPages
                      ? "bg-[#232F61] text-white"
                      : "hover:bg-gray-100"
                  }`}
                >
                  {totalPages}
                </button>
              )}

              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage === totalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
        </Revelar>

        {/* Altas recientes */}
        <Revelar className="h-full">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 h-full">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">
              Altas recientes
            </h3>
            <button
              onClick={() => navigate("/almacen/registro")}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              title="Ver todas las herramientas"
            >
              <ArrowUpRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          <div className="space-y-3">
            {isLoading ? (
              Array.from({ length: MAX_RECIENTES }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 bg-white"
                  style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
                >
                  <div className="w-9 h-9 bg-gray-100 rounded-lg animate-pulse flex-shrink-0" />
                  <div className="flex-1 space-y-1">
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                  </div>
                </div>
              ))
            ) : loadError ? (
              <SeccionError onRetry={loadData} />
            ) : recientes.length === 0 ? (
              <div className="flex items-center justify-center min-h-[120px] text-sm text-gray-400">
                Sin herramientas recientes
              </div>
            ) : (
              recientes.map((h) => (
                <button
                  key={h.id}
                  type="button"
                  onClick={() => navigate(`/almacen/registro/${h.id}`)}
                  className="w-full flex items-center gap-4 p-2 rounded-lg border border-gray-100 bg-white hover:bg-gray-50 transition-colors text-left"
                  style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
                >
                  <ToolImageWithFallback
                    imageUrl={h.photoUrl ?? undefined}
                    nombre={h.nombre}
                    className="flex-shrink-0"
                    style={{ width: 56, height: 49 }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {h.nombre}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatTimeShort(h.registradaEn)}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
        </Revelar>
      </div>
      </>
      )}
    </div>
  );
};

export default AlmacenDashboard;
